import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import WorldMapBackground from "@/components/WorldMapBackground";
import { useAuth } from "@/context/AuthContext";

function SocialButton({ children, brand, onClick }) {
  return (
    <button onClick={onClick} data-testid={`login-${brand}`}
      className="flex w-full items-center justify-center gap-3 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition-colors hover:bg-secondary">
      {children}
    </button>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const go = (user) => navigate(user.role === "recruiter" ? "/recruiter" : "/app");

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) { toast.success(`Welcome back, ${res.user.name}`); go(res.user); }
    else setError(res.error);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-10">
      <WorldMapBackground />
      <div className="absolute left-5 top-5"><Logo /></div>
      <div className="absolute right-5 top-5"><ThemeToggle /></div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md">
        <div className="card-surface p-8 shadow-float">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Welcome back</h1>
          <p className="mt-2 text-sm text-muted-foreground">Log in to your Career Profile.</p>

          <div className="mt-6 space-y-3">
            <SocialButton brand="google" onClick={() => toast("Social login is demo-only. Use email below.")}>
              <span className="font-bold text-electric">G</span> Continue with Google
            </SocialButton>
            <SocialButton brand="linkedin" onClick={() => toast("Social login is demo-only. Use email below.")}>
              <span className="font-bold text-[#0A66C2]">in</span> Continue with LinkedIn
            </SocialButton>
          </div>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs font-semibold text-muted-foreground">OR</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-caps">Email</label>
              <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-background px-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" className="w-full bg-transparent py-3 text-sm outline-none" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="label-caps">Password</label>
                <button type="button" onClick={() => toast("Password reset link sent (demo).")} className="text-xs font-semibold text-electric">Forgot?</button>
              </div>
              <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-background px-3">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <input data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="w-full bg-transparent py-3 text-sm outline-none" />
                <button type="button" onClick={() => setShow(!show)}>{show ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}</button>
              </div>
            </div>

            {error && <p data-testid="login-error" className="text-sm font-medium text-destructive">{error}</p>}

            <button type="submit" disabled={loading} data-testid="login-submit" className="cv-btn-primary w-full">
              {loading ? "Signing in…" : <>Log In <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New to CVOLVE? <Link to="/signup" className="font-semibold text-electric">Start free</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
